import { User, Cpu, LogOut, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store/useStore';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import api, { companyProfileApi } from '@/services/api';

export function Header() {
  const { t, i18n } = useTranslation();
  const { currentProject, isProcessing, selectedModel, setSelectedModel, generatedDocument } = useStore();
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const switchLanguage = async () => {
    const next = (i18n.language === 'de' || i18n.language?.startsWith('de-')) ? 'en' : 'de';
    i18n.changeLanguage(next);
    localStorage.setItem('ui_language', next);
    if (localStorage.getItem('auth_token')) {
      try {
        await api.put('/auth/profile', { language_preference: next });
      } catch { /* non-critical */ }
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get('/auth/me');
        setUserEmail(response.data.email);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    const fetchCompany = async () => {
      try {
        const data = await companyProfileApi.get();
        if (data) {
          setCompanyName(data.name || '');
          if (data.logo_url) setLogoUrl(data.logo_url);
        }
      } catch { /* no profile yet */ }
    };
    fetchUser();
    fetchCompany();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    navigate('/');
  };

  const downloadDocument = async () => {
    if (!generatedDocument) return;
    try {
      const response = await api.get(`/documents/${generatedDocument.docId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', generatedDocument.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Left side - company branding + breadcrumb */}
        <div className="flex-1 flex items-center gap-3 min-w-0">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className="h-8 w-auto object-contain shrink-0" />
          ) : null}
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-lg font-semibold shrink-0">{companyName || 'DocIntel'}</span>
            {currentProject && (
              <>
                <span className="text-muted-foreground shrink-0">/</span>
                <span className="text-sm text-muted-foreground truncate">{currentProject.name}</span>
              </>
            )}
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Processing indicator */}
          {isProcessing && (
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <div className="h-2 w-2 bg-primary rounded-full animate-pulse" />
              <span>{t('header.processing')}</span>
            </div>
          )}

          {/* Download Generated Document */}
          {generatedDocument && (
            <Button
              onClick={downloadDocument}
              variant="default"
              size="sm"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('header.downloadGenerated')}
            </Button>
          )}

          {/* Global Model Selector - Only show in development */}
          {import.meta.env.MODE === 'development' && (
            <div className="flex items-center gap-2">
              <Cpu className="h-4 w-4 text-muted-foreground" />
              <Select value={selectedModel || 'claude'} onValueChange={setSelectedModel}>
                <SelectTrigger className="w-[160px] bg-background border-2">
                  <SelectValue placeholder={t('header.remoteModel')} />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="claude">☁️ {t('header.remoteModel')}</SelectItem>
                  <SelectItem value="ollama">💻 {t('header.localModel')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Language picker – flag of the OTHER language (click to switch) */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Switch language"
            onClick={switchLanguage}
            className="text-lg"
          >
            {(i18n.language === 'de' || i18n.language?.startsWith('de-')) ? '🇺🇸' : '🇩🇪'}
          </Button>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('header.userMenu')}>
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {userEmail && (
                <>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{t('header.account')}</p>
                      <p className="text-xs leading-none text-muted-foreground truncate">
                        {userEmail}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                <span>{t('header.logout')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
